import React, { useEffect, useState } from "react";
import { Check, Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { AgreementRecord } from "@/types/agreement";

interface SignerRow {
  name: string;
  title: string;
  email: string;
}

/**
 * Sends an agreement for signing: who signs for the customer (they sign in this order, then the company counter-signs),
 * and whether the first signer is emailed their link or the rep gets the link to share. Sending freezes the content.
 */
export function SendAgreementDialog({
  open,
  onOpenChange,
  agreement,
  onSent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agreement: AgreementRecord;
  onSent: (rec: AgreementRecord) => void;
}) {
  const { success, error } = useToast();
  const [channel, setChannel] = useState<"EMAIL" | "LINK">("EMAIL");
  const [signers, setSigners] = useState<SignerRow[]>([]);
  const [message, setMessage] = useState("");
  const [attach, setAttach] = useState(true);
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState<Array<{ name: string; url: string }> | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const c = agreement.contact;
    setSigners([{ name: c?.name || "", title: c?.job_title || "", email: c?.email || "" }]);
    setChannel("EMAIL");
    setMessage("");
    setAttach(true);
    setLinks(null);
    setCopied(false);
  }, [open, agreement.id]);

  const set = (i: number, patch: Partial<SignerRow>) => setSigners((rows) => rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signers.some((s) => s.name.trim().length < 2)) return error("Check the signers", "Every signer needs a name");
    if (channel === "EMAIL" && signers.some((s) => !s.email.trim())) return error("Check the signers", "Every signer needs an email address to send by email");
    setBusy(true);
    try {
      const res = unwrapObject<{ via: string; links: Array<{ name: string; url: string }>; agreement: AgreementRecord }>(
        await api.post(`/api/Agreement/Send/${agreement.id}`, {
          channel,
          signers: signers.map((s) => ({ name: s.name.trim(), title: s.title.trim() || undefined, email: s.email.trim() || undefined })),
          message: message.trim() || undefined,
          attach_pdf: channel === "EMAIL" ? attach : undefined,
        }),
      );
      onSent(res.agreement);
      if (channel === "LINK" && res.links.length) setLinks(res.links);
      else {
        success("Sent for signing", `${agreement.display_number} went ${res.via}`);
        onOpenChange(false);
      }
    } catch (err: any) {
      error("Couldn't send it", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      error("Couldn't copy", "Select the link and copy it by hand");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{links ? "Share the signing link" : `Send ${agreement.display_number} for signing`}</DialogTitle>
          <DialogDescription>
            {links
              ? "Anyone with this link can sign as the person it was made for, so share it only with them."
              : "Sending locks the agreement: what the signers see is exactly what they sign. To change it afterwards, make a new version."}
          </DialogDescription>
        </DialogHeader>

        {links ? (
          <div className="space-y-3">
            {links.map((l) => (
              <div key={l.url} className="space-y-1.5">
                <Label className="text-xs">Link for {l.name}</Label>
                <div className="flex gap-2">
                  <Input readOnly value={l.url} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
                  <Button type="button" variant="outline" onClick={() => copy(l.url)} className="gap-1.5 shrink-0">
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />} {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">This link won't be shown again. Later signers get theirs from the agreement page when it's their turn.</p>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Signers for {agreement.account?.name || "the customer"}, in signing order</Label>
              {signers.map((s, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1.4fr_auto] gap-2 items-center">
                  <Input placeholder="Full name" value={s.name} maxLength={120} onChange={(e) => set(i, { name: e.target.value })} />
                  <Input placeholder="Job title" value={s.title} maxLength={120} onChange={(e) => set(i, { title: e.target.value })} />
                  <Input type="email" placeholder={channel === "EMAIL" ? "Email" : "Email (optional)"} value={s.email} maxLength={200} onChange={(e) => set(i, { email: e.target.value })} />
                  <Button type="button" variant="ghost" size="icon" disabled={signers.length === 1} onClick={() => setSigners((r) => r.filter((_, k) => k !== i))} aria-label="Remove signer">
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
              {signers.length < 5 && (
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setSigners((r) => [...r, { name: "", title: "", email: "" }])}>
                  <Plus className="size-3.5" /> Add another signer
                </Button>
              )}
              <p className="text-[11px] text-muted-foreground">After the customer's signers, a Super Admin counter-signs for {"the company"}.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">How to send it</Label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    ["EMAIL", "Email the first signer", "They get a link by email; the next signer is emailed when it's their turn."],
                    ["LINK", "Just make a link", "Nothing is emailed. You share the link yourself."],
                  ] as const
                ).map(([id, label, hint]) => (
                  <button key={id} type="button" onClick={() => setChannel(id)} className={cn("text-left rounded-lg border px-3 py-2 transition-colors", channel === id ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50")}>
                    <div className="text-sm font-semibold">{label}</div>
                    <div className="text-[11px] text-muted-foreground">{hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {channel === "EMAIL" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs">Message (optional)</Label>
                  <Textarea rows={3} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A short note to go with the link" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} className="size-4 rounded border-input" />
                  Attach a PDF copy
                </label>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="size-4 animate-spin mr-2" />}
                {channel === "EMAIL" ? "Send for signing" : "Create link"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
